import React from 'react';
import { User, Camera, MapPin, Shield, Fingerprint, Gift } from 'lucide-react';

const ProfileCompletionModal = ({
  userProfile,
  setUserProfile,
  handleProfilePicUpload,
  searchAddress,
  addressSuggestions,
  showAddressSuggestions,
  selectAddress,
  setShowTwoFactorSetup,
  twoFactorEnabled,
  biometricAvailable,
  biometricEnabled,
  enableBiometric,
  handleProfileSubmit
}) => {
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-md rounded-3xl p-8 shadow-2xl my-8">
        <div className="text-center mb-8">
          <div className="relative w-24 h-24 mx-auto mb-4">
            <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center overflow-hidden">
              {userProfile.profilePic ? (
                <img src={userProfile.profilePic} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <User size={40} className="text-purple-600" />
              )}
            </div>
            <label className="absolute bottom-0 right-0 bg-purple-600 text-white p-2 rounded-full cursor-pointer hover:bg-purple-700 transition-all shadow-lg">
              <Camera size={16} />
              <input
                type="file"
                accept="image/*"
                onChange={handleProfilePicUpload}
                className="hidden"
              />
            </label>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Complete Your Profile</h2>
          <p className="text-gray-600">Help us serve you better</p>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={userProfile.name}
              onChange={(e) => setUserProfile({ ...userProfile, name: e.target.value })}
              placeholder="Enter your full name"
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-purple-500 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={userProfile.email}
              onChange={(e) => setUserProfile({ ...userProfile, email: e.target.value })}
              placeholder="your.email@example.com"
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-purple-500 transition-all"
            />
          </div>

          <div className="relative">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Delivery Address
              <span className="text-xs font-normal text-gray-500 ml-2">
                (Start typing for suggestions)
              </span>
            </label>
            <div className="relative">
              <textarea
                value={userProfile.address}
                onChange={(e) => searchAddress(e.target.value)}
                placeholder="Start typing your address..."
                rows="3"
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-purple-500 transition-all resize-none"
              />
              <MapPin size={20} className="absolute right-4 top-3 text-gray-400" />
            </div>
            
            {showAddressSuggestions && addressSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border-2 border-purple-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                {addressSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => selectAddress(suggestion)}
                    className="w-full text-left px-4 py-3 hover:bg-purple-50 transition-all border-b last:border-b-0"
                  >
                    <div className="flex items-start gap-2">
                      <MapPin size={16} className="text-purple-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-800">{suggestion.address}</p>
                        <p className="text-xs text-gray-500 mt-1">Pincode: {suggestion.pincode}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              City
            </label>
            <input
              type="text"
              value={userProfile.city}
              onChange={(e) => setUserProfile({ ...userProfile, city: e.target.value })}
              placeholder="Enter your city"
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-purple-500 transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Pincode
            </label>
            <input
              type="text"
              maxLength="6"
              value={userProfile.pincode}
              onChange={(e) => setUserProfile({ ...userProfile, pincode: e.target.value.replace(/\D/g, '') })}
              placeholder="Enter 6-digit pincode"
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-purple-500 transition-all"
            />
          </div>
        </div>

        {/* Security Features */}
        <div className="space-y-3 mb-6">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield size={24} className="text-blue-600" />
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm">Two-Factor Authentication</h4>
                  <p className="text-xs text-gray-600">Add extra security to your account</p>
                </div>
              </div>
              <button
                onClick={() => setShowTwoFactorSetup(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-xs font-medium hover:bg-blue-700 transition-all"
              >
                {twoFactorEnabled ? 'Enabled' : 'Enable'}
              </button>
            </div>
          </div>

          {biometricAvailable && (
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Fingerprint size={24} className="text-purple-600" />
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">Biometric Login</h4>
                    <p className="text-xs text-gray-600">Use fingerprint or face ID</p>
                  </div>
                </div>
                <button
                  onClick={enableBiometric}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg text-xs font-medium hover:bg-purple-700 transition-all"
                >
                  {biometricEnabled ? 'Enabled' : 'Enable'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Gift size={20} className="text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 text-sm mb-1">Welcome Offer! 🎉</h4>
              <p className="text-xs text-gray-600">
                Complete your profile and get ₹50 off on your first order above ₹200
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={handleProfileSubmit}
          className="w-full bg-purple-600 text-white py-4 rounded-xl font-semibold hover:bg-purple-700 transition-all"
        >
          Complete Profile & Start Shopping
        </button>

        <p className="text-xs text-gray-500 text-center mt-4">
          * Required fields
        </p>
      </div>
    </div>
  );
};

export default ProfileCompletionModal;